/**
 * Replay-time rules for plan steps saved with recording status markers.
 * Keep in sync with nlp_automation_phase2/src/utils/replayEligibility.js
 */
export const STEP_RECORDING_STATUS = {
    PENDING: 'pending',
    COMPLETED: 'completed',
    FAILED: 'failed',
    FAILED_BY_EVALUATION: 'failed_by_evaluation',
};

/** Legacy plans without status still replay; non-replayable recording statuses are skipped. */
export function shouldSkipStepForReplay(recordingStatus, step) {
    if (step?.customStep === true) return false;
    if (step?.replayable === false) return true;
    if (!recordingStatus) return false;
    return (
        recordingStatus === STEP_RECORDING_STATUS.PENDING ||
        recordingStatus === STEP_RECORDING_STATUS.FAILED ||
        recordingStatus === STEP_RECORDING_STATUS.FAILED_BY_EVALUATION
    );
}

export function isRecordingStatus(status) {
    return (
        status === STEP_RECORDING_STATUS.PENDING ||
        status === STEP_RECORDING_STATUS.COMPLETED ||
        status === STEP_RECORDING_STATUS.FAILED ||
        status === STEP_RECORDING_STATUS.FAILED_BY_EVALUATION
    );
}

/**
 * Plans exported from the recorder use `completed` / `failed_by_evaluation`;
 * AI-generated steps use `success` / `running`.
 */
export function isRecordedPlanContext(instructions = []) {
    let hasRecordingMarker = false;
    let hasAiGenMarker = false;

    for (const inst of instructions || []) {
        for (const step of inst.aiSteps || []) {
            const s = step?.status;
            if (s === 'success' || s === 'running') {
                hasAiGenMarker = true;
            }
            if (isRecordingStatus(s)) {
                hasRecordingMarker = true;
            }
            if (typeof step?.replayable === 'boolean') {
                hasRecordingMarker = true;
            }
        }
    }

    return hasRecordingMarker && !hasAiGenMarker;
}

/**
 * Map recording-time plan statuses to execution UI statuses.
 * Recorder plans use `completed` / `pending` / `failed`; replay + Generate AI use `success` / `running`.
 *
 * `failed_by_evaluation` — browser step completed; a later evaluateTestStep failed (plan export mark).
 * The action itself succeeded — do not show as failed or skip on replay.
 */
export function mapRecordingStatusForDisplay(status) {
    if (status === STEP_RECORDING_STATUS.COMPLETED) return 'success';
    if (status === STEP_RECORDING_STATUS.FAILED_BY_EVALUATION) return 'success';
    if (status === STEP_RECORDING_STATUS.FAILED) return 'failed';
    return status;
}

/** Step failed during recording — grey skip UI + read-only editor. Pending recording steps stay pending until replay. */
export function isRecordingSkipMarkerStep(step, instructions = []) {
    if (step?.customStep) return false;
    if (step?.replayable === false) return isRecordedPlanContext(instructions);
    if (
        step?.status !== STEP_RECORDING_STATUS.FAILED &&
        step?.status !== STEP_RECORDING_STATUS.FAILED_BY_EVALUATION
    ) {
        return false;
    }
    return isRecordedPlanContext(instructions);
}

/**
 * Saved recorded plan is open and nothing has run in this session yet.
 * Recording outcomes (completed/failed) must not paint pass/fail chrome until replay.
 */
export function isSavedRecordedPlanIdleView({
    instructions = [],
    hasExecutedInSession = false,
    isRunning = false,
    isReplayExecuting = false,
    isGenerating = false,
} = {}) {
    if (!isRecordedPlanContext(instructions)) return false;
    if (hasExecutedInSession || isRunning || isReplayExecuting || isGenerating) return false;
    return true;
}

/**
 * Grey skip styling + read-only editor.
 * - Idle saved recorded plan (on load)
 * - Replay flow (prep / running / finished)
 * Not during live recording/generation (pending = in progress).
 */
export function shouldShowRecordingSkipUi(step, instructions = [], options = {}) {
    const opts =
        typeof options === 'boolean' ? { isReplayFlow: options } : options;
    const {
        isReplayFlow = false,
        isSavedPlanIdle = false,
        isLiveRunActive = false,
        replayStatus = null,
    } = opts;

    if (isLiveRunActive) return false;

    const isRecordingSkipMarker = isRecordingSkipMarkerStep(step, instructions);

    // Idle saved plan: grey styling from recording markers only (no replay session yet).
    if (isSavedPlanIdle) {
        return isRecordingSkipMarker;
    }

    // During replay, the recording-specific skip notice is only for plan markers
    // from a failed recording step. Normal replay skips (abort, downstream skip,
    // backend skip) should render as skipped result status, not recording chrome.
    if (isReplayFlow) {
        return isRecordingSkipMarker;
    }

    return false;
}

/** @deprecated use shouldShowRecordingSkipUi — kept for replay parity call sites */
export function isStepSkippedInRecordedReplay(step, instructions = [], options = false) {
    return shouldShowRecordingSkipUi(step, instructions, options);
}

/** Hide recording pass/fail on non-skip steps until replay runs in this session. */
export function shouldSuppressRecordedOutcomeOnIdlePlan(step, instructions = [], isSavedPlanIdle = false) {
    if (!isSavedPlanIdle) return false;
    if (isRecordingSkipMarkerStep(step, instructions)) return false;
    return true;
}

/** Replay statuses that reflect this replay run (not stale recording outcomes). */
const TERMINAL_REPLAY_OUTCOME = new Set(['Passed', 'Failed', 'Skipped']);

function isReplayQueuedStatus(replayStatus) {
    return !replayStatus || replayStatus === 'New' || replayStatus === 'Pending';
}

/**
 * Hide recording pass/fail while a step is queued or running in replay.
 * Skip-marker rows use skip styling instead.
 */
export function shouldSuppressRecordingOutcomeDuringReplay(
    step,
    instructions = [],
    replayStatus,
    { isReplayFlow = false } = {}
) {
    if (!isReplayFlow) return false;
    if (isRecordingSkipMarkerStep(step, instructions)) return false;
    if (isReplayQueuedStatus(replayStatus)) return true;
    if (replayStatus === 'In-Progress') return false;
    if (TERMINAL_REPLAY_OUTCOME.has(replayStatus)) return false;
    return true;
}

/** True when replay has not assigned a terminal outcome to this step yet. */
export function isReplayStepQueued(isReplayFlow, replayStatus, isRecordingSkipLocked = false) {
    if (!isReplayFlow || isRecordingSkipLocked) return false;
    return isReplayQueuedStatus(replayStatus);
}

export function getRecordingSkipReason(recordingStatus) {
    if (recordingStatus === STEP_RECORDING_STATUS.PENDING) {
        return 'This step was pending during recording and will be skipped when you replay.';
    }
    if (recordingStatus === STEP_RECORDING_STATUS.FAILED) {
        return 'This step failed during recording and will be skipped when you replay.';
    }
    if (recordingStatus === STEP_RECORDING_STATUS.FAILED_BY_EVALUATION) {
        return 'This step failed validation during recording and will be skipped when you replay.';
    }
    return 'This step will be skipped when you replay.';
}
